/**
 * OB dating concordance logic.
 * Shared by the OB forms (form display) and their PDF generators (PDF output)
 * so the two never drift — same pattern as lungScreeningEligibility.js.
 *
 * Redating thresholds per ACOG Committee Opinion 700, as supplied by the user:
 * a discrepancy above the threshold for the gestational age by LMP is discordant.
 */

// [last gestational day of the range, days of discrepancy allowed]
const REDATING_THRESHOLDS = [
  [8 * 7 + 6, 5],    // <= 8w6d
  [13 * 7 + 6, 7],   // 9w0d - 13w6d
  [15 * 7 + 6, 7],   // 14w0d - 15w6d
  [21 * 7 + 6, 10],  // 16w0d - 21w6d
  [27 * 7 + 6, 14]   // 22w0d - 27w6d
];
const REDATING_THRESHOLD_28_WEEKS_PLUS = 21;

export function getRedatingThresholdDays(gestationalAgeDays) {
  const range = REDATING_THRESHOLDS.find(([lastDay]) => gestationalAgeDays <= lastDay);
  return range ? range[1] : REDATING_THRESHOLD_28_WEEKS_PLUS;
}

/**
 * Compare ultrasound dates against clinical dates.
 *
 * @param {{weeks: string, days: string}} ultrasoundEGA
 * @param {{weeks: string, days: string}} clinicalEGA
 * @returns {{ text: string, isDiscordant: boolean }|null} null unless both are entered
 */
export function getConcordance(ultrasoundEGA, clinicalEGA) {
  const usWeeks = parseInt(ultrasoundEGA?.weeks) || 0;
  const usDays = parseInt(ultrasoundEGA?.days) || 0;
  const clinicalWeeks = parseInt(clinicalEGA?.weeks) || 0;
  const clinicalDays = parseInt(clinicalEGA?.days) || 0;

  if ((!usWeeks && !usDays) || (!clinicalWeeks && !clinicalDays)) {
    return null;
  }

  const usTotalDays = (usWeeks * 7) + usDays;
  const clinicalTotalDays = (clinicalWeeks * 7) + clinicalDays;
  const difference = usTotalDays - clinicalTotalDays;

  if (difference === 0) {
    return { text: 'Size concordant with dates', isDiscordant: false };
  }

  const absDiff = Math.abs(difference);
  const weeks = Math.floor(absDiff / 7);
  const days = absDiff % 7;

  let text = 'Size ';

  if (weeks > 0 && days > 0) {
    text += `${weeks} week${weeks > 1 ? 's' : ''} ${days} day${days > 1 ? 's' : ''}`;
  } else if (weeks > 0) {
    text += `${weeks} week${weeks > 1 ? 's' : ''}`;
  } else {
    text += `${days} day${days > 1 ? 's' : ''}`;
  }

  text += difference > 0 ? ' ahead of dates' : ' less than dates';

  return { text, isDiscordant: absDiff > getRedatingThresholdDays(clinicalTotalDays) };
}
